import type { ActivityOf } from '@wyn/content';
import { createRng } from '../rng.ts';
import { BTN, DT, type RealtimeSim, TICK_HZ } from './realtime.ts';
import { clamp, type Fx, type MinigameResult } from './types.ts';

/** Hold BTN.left / BTN.right to slide the pick; BTN.main (pressed) lifts the pin. */
export interface LockpickingSim extends RealtimeSim {
  /** Pick position along the keyway, 2–98. */
  pick: number;
  /** Pins set so far. */
  pin: number;
  pins: number;
  /** 0–100; at 100 the pick snaps. */
  strain: number;
  /** Seconds of lantern left. */
  timeLeft: number;
  /** How close the pick is to the sweet spot, 0–1 (what the player feels). */
  feel: number;
  /** The current pin's sweet spot. Not drawn, but the client could compute it from the seed anyway. */
  sweet: number;
  /** A security pin's false set, or null. It feels as strong as the sweet spot, but the feel throbs. */
  decoy: number | null;
  tolerance: number;
}

const SLIDE = 28;
/** A false set is at least this far along the keyway from the true one. */
const DECOY_GAP = 25;
/** How fast a false set throbs, per second. */
const THROB_HZ = 2.5;
const FALSE_SET_STRAIN = 22;

/**
 * Locksmiths' Row. Slide the pick until it "feels" right, then lift the pin. A wrong lift strains
 * the pick; finish before the watchman's lantern burns out. Ported from the Harrowmere prototype.
 * From the coffer up, some pins are security pins: a second spot feels just as strong, but its feel
 * throbs where the true one holds steady. Lifting there is a false set. The lockpick is escrowed at
 * the start and handed back unless it snaps.
 */
export function lockpickingSim(a: ActivityOf<'lockpicking'>, level: number, seed: string): LockpickingSim {
  const rng = createRng(seed);
  const rand = (lo: number, hi: number) => lo + rng.next() * (hi - lo);
  let prev = 0;

  const fx = (text: string, tone: Fx['tone'], cue?: string) => sim.fx.push({ tick: sim.tick, text, tone, cue });
  const end = (r: MinigameResult) => void (sim.result = r);
  const pickBack = { itemKey: 'lockpick', quantity: 1, graded: false, quality: 'common' as const };

  const win = () => {
    const coin = Math.round(rand(a.loot[0], a.loot[1]) * (0.75 + (0.5 * sim.timeLeft) / a.time));
    fx('The lock springs open!', 'good', 'open');
    end({
      score: 0.5 + 0.5 * (1 - sim.strain / 100),
      title: 'The lock gives way',
      text: `You slip away with ${coin} coin from the ${a.name.toLowerCase()}.`,
      breakdown: [['Pick condition', 1 - sim.strain / 100], ['Time to spare', sim.timeLeft / a.time]],
      yield: pickBack,
      coin,
      xpMult: 1,
      // Every pin set without a single wrong lift.
      feats: sim.strain === 0 ? ['clean-pick'] : [],
    });
  };
  const fail = (why: 'snap' | 'time') => {
    fx(why === 'snap' ? 'SNAP! The pick breaks' : 'Footsteps! You flee', 'bad', why);
    end({
      score: (sim.pin / a.pins) * 0.4,
      title: why === 'snap' ? 'Your pick snaps' : 'The watch is coming',
      text: why === 'snap' ? 'Your lockpick snaps in the keyway.' : 'You melt into the shadows empty-handed.',
      breakdown: [['Pins set', sim.pin / a.pins]],
      yield: why === 'snap' ? null : pickBack,
      coin: 0,
      xpMult: 1,
      feats: why === 'snap' ? ['pick-snapped'] : [],
    });
  };

  const nextPin = () => {
    sim.sweet = rand(8, 92);
    sim.decoy = null;
    if (a.security && rng.next() < a.security) {
      // Either side of the sweet spot, wherever there's room for the gap.
      const sides = [sim.sweet - DECOY_GAP - rand(0, 30), sim.sweet + DECOY_GAP + rand(0, 30)].filter((x) => x >= 8 && x <= 92);
      sim.decoy = sides.length ? sides[rng.int(0, sides.length - 1)]! : null;
    }
  };

  const lift = () => {
    const d = Math.abs(sim.pick - sim.sweet);
    if (d <= sim.tolerance) {
      sim.pin++;
      fx('Click!', 'good', 'click');
      if (sim.pin >= a.pins) return win();
      nextPin();
    } else if (sim.decoy !== null && Math.abs(sim.pick - sim.decoy) <= sim.tolerance) {
      sim.strain += FALSE_SET_STRAIN;
      fx('A false set! The pin springs back', 'bad', 'false-set');
      if (sim.strain >= 100) fail('snap');
    } else {
      sim.strain += 18 + d * 0.4;
      fx(d < sim.tolerance * 2.5 ? 'Close… scrape' : 'Scrape!', 'bad', 'scrape');
      if (sim.strain >= 100) fail('snap');
    }
  };

  const sim: LockpickingSim = {
    tick: 0,
    result: null,
    fx: [],
    pick: 50,
    pin: 0,
    pins: a.pins,
    strain: 0,
    timeLeft: a.time,
    feel: 0,
    sweet: 0,
    decoy: null,
    // Practice beyond the lock's own level widens the feel for the pins; a harder lock starts tighter.
    tolerance: clamp(7.5 - a.pins * 0.5 + Math.max(0, level - a.level) * 0.15, 2, 8),
    step(mask) {
      if (sim.result) return;
      sim.tick++;
      const pressed = (mask & BTN.main) !== 0 && (prev & BTN.main) === 0;
      prev = mask;
      if (pressed) {
        lift();
        if (sim.result) return;
      }
      const dir = (mask & BTN.right ? 1 : 0) - (mask & BTN.left ? 1 : 0);
      sim.pick = clamp(sim.pick + dir * SLIDE * DT, 2, 98);
      sim.timeLeft -= DT;
      const near = (at: number) => clamp(1 - Math.abs(sim.pick - at) / 35, 0, 1) ** 2;
      const throb = 0.75 + 0.25 * Math.sin(2 * Math.PI * THROB_HZ * sim.tick / TICK_HZ);
      sim.feel = Math.max(near(sim.sweet), sim.decoy === null ? 0 : near(sim.decoy) * throb);
      if (sim.timeLeft <= 0 || sim.tick >= a.time * TICK_HZ + 1) fail('time');
    },
  };
  nextPin();
  return sim;
}
